/* Cloud-Wartung & Auto-Updates (Superadmin).
 *
 * - Update-Verfügbarkeit (laufende vs. neueste NC-Point-Version) via /update-status
 * - SOFORT UPDATE (Admin) → POST /minor-update/now (läuft über die Queue)
 * - Auto-Minor-Update-Toggle (An / Aus / globaler Default)
 * - "Update jetzt anfordern" → läuft im nächsten Maintenance-Window
 * - Zeigt das effektive Maintenance-Window (UTC-Stunde, identisch zu Tunables)
 */
import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { CalendarClock, Zap, X, CheckCircle2, RefreshCw, ArrowUpCircle, PackageCheck, ShieldAlert, Wrench } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CloudMaintenancePanel({ cloudId, cloud, onChange }) {
    const { data: st, mutate: refetchStatus } = useSWR(
        `/api/admin/clouds/${cloudId}/update-status`, fetcher, { refreshInterval: 20000 });
    const { data: ai, mutate: refetchAi } = useSWR(
        `/api/admin/clouds/${cloudId}/app-integrity`, fetcher, { refreshInterval: 30000 });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [msg, setMsg] = useState(null);

    const autoVal =
        cloud?.auto_minor_update === true ? "on"
            : cloud?.auto_minor_update === false ? "off" : "";
    const autoAppVal =
        cloud?.auto_app_update === true ? "on"
            : cloud?.auto_app_update === false ? "off" : "";
    const winHour = Number.isInteger(cloud?.maintenance_window_start)
        ? String(cloud.maintenance_window_start).padStart(2, "0") + ":00 UTC"
        : "global default (01:00 UTC)";
    const requested = cloud?.minor_update_requested_at;
    const available = st?.update_available;
    const corruptApps = (ai?.corrupt || []).map((c) => c.app || c);

    const call = async (fn) => {
        setBusy(true); setErr(null); setMsg(null);
        try { await fn(); if (onChange) await onChange(); await refetchStatus(); }
        catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const setAuto = (v) => call(async () => {
        const value = v === "on" ? true : v === "off" ? false : null;
        await api.patch(`/api/admin/clouds/${cloudId}`, { auto_minor_update: value });
    });
    const setAutoApp = (v) => call(async () => {
        const value = v === "on" ? true : v === "off" ? false : null;
        await api.patch(`/api/admin/clouds/${cloudId}`, { auto_app_update: value });
    });
    const rescanApps = () => call(async () => {
        await api.get(`/api/admin/clouds/${cloudId}/app-integrity?refresh=true`);
        await refetchAi();
        setMsg("App integrity scan completed.");
    });
    const repairApps = () => call(async () => {
        const { data } = await api.post(`/api/admin/clouds/${cloudId}/app-integrity/repair`, {});
        await refetchAi();
        setMsg(data.already_running
            ? "An app repair is already running."
            : "App repair queued — see Jobs/Timeline for progress.");
    });
    const requestNow = () => call(async () => {
        await api.post(`/api/admin/clouds/${cloudId}/minor-update/request`);
    });
    const cancelRequest = () => call(async () => {
        await api.delete(`/api/admin/clouds/${cloudId}/minor-update/request`);
    });
    const sofortUpdate = () => {
        if (!window.confirm(
            `Start UPDATE NOW for "${cloud?.name}"?\n\n` +
            `NC ${st?.current || "?"} → ${st?.latest || "?"}. The cloud enters ` +
            `maintenance mode during the update (briefly unreachable). ` +
            `Runs through the job queue.`)) return;
        call(async () => {
            const { data } = await api.post(`/api/admin/clouds/${cloudId}/minor-update/now`);
            setMsg(data.already_running
                ? "A maintenance job is already running."
                : "UPDATE NOW queued — see Jobs/Timeline for progress.");
        });
    };

    return (
        <div className="cm-card p-5" data-testid="cloud-maintenance-panel">
            <div className="cm-h3 mb-3 flex items-center gap-2">
                <CalendarClock size={14} className="text-zinc-400" />
                Maintenance &amp; auto updates
            </div>
            <p className="text-xs text-zinc-400 mb-4">
                NC minor/point updates (e.g. 34.0.0 → 34.0.1) run in this
                cloud's maintenance window (identical to the window in the
                tunables). updater.phar runs serialized across worker + web VMs;
                as a last step the backup starts — if configured.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-zinc-950 border border-zinc-800 p-2.5">
                    <div className="cm-label mb-1">Current</div>
                    <div className="text-sm text-zinc-200" data-testid="maint-current-value">
                        {st?.current || "unknown"}
                    </div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-2.5">
                    <div className="cm-label mb-1">Latest</div>
                    <div className="text-sm text-zinc-200" data-testid="maint-latest-value">
                        {st?.latest || "—"}
                    </div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-2.5">
                    <div className="cm-label mb-1">Window</div>
                    <div className="text-sm text-zinc-200" data-testid="maint-window-value">{winHour}</div>
                </div>
            </div>

            {available && (
                <div
                    data-testid="maint-update-available"
                    className="mb-3 flex items-center justify-between gap-2 bg-emerald-950/30 border border-emerald-800 p-2.5 text-xs"
                >
                    <span className="flex items-center gap-1.5 text-emerald-300">
                        <ArrowUpCircle size={14} />
                        Update available: {st.current} → {st.latest}
                    </span>
                    <button
                        data-testid="maint-sofort-button"
                        className="cm-btn-primary text-xs flex items-center gap-1 shrink-0"
                        disabled={busy}
                        onClick={sofortUpdate}
                    >
                        {busy ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />}
                        UPDATE NOW
                    </button>
                </div>
            )}

            <label className="block mb-4">
                <div className="cm-label mb-1.5">Automatic minor updates</div>
                <select
                    data-testid="maint-auto-select"
                    value={autoVal}
                    disabled={busy}
                    onChange={(e) => setAuto(e.target.value)}
                    className="cm-input text-xs w-full"
                >
                    <option value="">— global default (OFF) —</option>
                    <option value="on">On — update automatically in the window</option>
                    <option value="off">Off — manual/on request only</option>
                </select>
            </label>

            <label className="block mb-4">
                <div className="cm-label mb-1.5 flex items-center gap-1.5">
                    <PackageCheck size={12} className="text-zinc-400" />
                    Automatic app updates (occ app:update --all)
                </div>
                <select
                    data-testid="maint-auto-app-select"
                    value={autoAppVal}
                    disabled={busy}
                    onChange={(e) => setAutoApp(e.target.value)}
                    className="cm-input text-xs w-full"
                >
                    <option value="">— global default (OFF) —</option>
                    <option value="on">On — update apps automatically in the window</option>
                    <option value="off">Off</option>
                </select>
                <p className="text-xs text-zinc-500 mt-1">
                    Runs in the same maintenance window, independent of the NC point update.
                    While running, the cloud shows the maintenance splash.
                </p>
            </label>

            <div className="mb-4 bg-zinc-950 border border-zinc-800 p-2.5"
                 data-testid="maint-app-integrity">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="cm-label flex items-center gap-1.5">
                        {corruptApps.length > 0
                            ? <ShieldAlert size={12} className="text-amber-400" />
                            : <CheckCircle2 size={12} className="text-emerald-400" />}
                        App file integrity
                    </div>
                    <button
                        data-testid="maint-app-rescan-button"
                        className="cm-btn-secondary text-[11px] flex items-center gap-1 shrink-0"
                        disabled={busy}
                        onClick={rescanApps}
                    >
                        <RefreshCw size={10} className={busy ? "animate-spin" : ""} /> Rescan
                    </button>
                </div>
                {corruptApps.length > 0 ? (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-amber-300"
                              data-testid="maint-app-corrupt-list">
                            {corruptApps.length} corrupt app(s): {corruptApps.join(", ")}
                        </span>
                        <button
                            data-testid="maint-app-repair-button"
                            className="cm-btn-primary text-[11px] flex items-center gap-1 shrink-0"
                            disabled={busy || ai?.repair_running}
                            onClick={repairApps}
                        >
                            <Wrench size={10} /> {ai?.repair_running ? "running…" : "Repair"}
                        </button>
                    </div>
                ) : (
                    <div className="text-xs text-zinc-500" data-testid="maint-app-ok">
                        No 0-byte corruption detected. Self-healing is active
                        (worker reports, CM repairs automatically).
                    </div>
                )}
            </div>

            {requested ? (
                <div
                    data-testid="maint-request-active"
                    className="flex items-center justify-between gap-2 bg-blue-950/30 border border-blue-800 p-2.5 text-xs"
                >
                    <span className="flex items-center gap-1.5 text-blue-300">
                        <CheckCircle2 size={13} />
                        Update requested — runs in the next maintenance window.
                    </span>
                    <button
                        data-testid="maint-cancel-button"
                        className="cm-btn-secondary text-xs flex items-center gap-1 shrink-0"
                        disabled={busy}
                        onClick={cancelRequest}
                    >
                        <X size={11} /> Cancel
                    </button>
                </div>
            ) : (
                <button
                    data-testid="maint-request-button"
                    className="cm-btn-secondary w-full text-xs flex items-center justify-center gap-1.5"
                    disabled={busy}
                    onClick={requestNow}
                >
                    {busy ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                    Schedule update in next window
                </button>
            )}

            {msg && (
                <div data-testid="maint-msg" className="mt-3 text-emerald-300 text-xs">{msg}</div>
            )}
            {err && (
                <div
                    data-testid="maint-error"
                    className="mt-3 bg-rose-950/30 border border-rose-800 text-rose-300 text-xs p-2 font-mono"
                >
                    {err}
                </div>
            )}
        </div>
    );
}
